import { BasicFilterSelectField, InputField, TypeaheadSelectField } from 'lib/iterable-hook-form';
import {
  HiddenVariantFormField,
  SelectFormFieldWrapper,
  StyledVariantFormField,
} from 'modules/experiments/components/pages/edit/sections/variant_configuration/CommonStyled';
import { useTakeoverExperimentContext, useVariantCardContext } from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { ComboTextFieldProps } from 'modules/experiments/types';
import { getFieldTemplateErrors } from 'modules/experiments/utils';
import { FieldNames } from 'modules/templateEditor/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSchemaValidationErrorTranslations } from 'utils/hooks/useTranslation';
import { ReadOnlyValue } from '../ReadOnlyValue';

export const EmailSender = ({ formFields, fieldValues, isFieldEditable }: ComboTextFieldProps) => {
  const {
    formState: { errors },
    setValue,
  } = useFormContext();
  const { zt } = useSchemaValidationErrorTranslations();
  const { t } = useExperimentsTranslations();
  const {
    fromNameAndSenderMetaInfo: { useDynamicSenders, emailSenders },
  } = useTakeoverExperimentContext();
  const { templateErrors } = useVariantCardContext();
  const isMountRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [fromNameFormField, sendersEmailFormField, replyToFormField] = formFields;
  const sendersEmailFieldTemplateErrors = getFieldTemplateErrors(
    templateErrors,
    FieldNames.FROM_EMAIL,
  );
  const watchedSendersEmail = useWatch({ name: sendersEmailFormField.key });

  const errorKey = errors[sendersEmailFormField.key]?.message?.toString() ?? '';
  const errorText = zt(errorKey) || sendersEmailFieldTemplateErrors;
  const hasError = !!errors[sendersEmailFormField.key] || !!sendersEmailFieldTemplateErrors?.length;
  const helperText = useDynamicSenders ? t('variantCard.helperText.senderEmail') : '';

  const memoizedSelectedOptions = useMemo(() => {
    return searchTerm
      ? emailSenders.filter(
          ({ doubleLine, label }) =>
            (label as string)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doubleLine?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : emailSenders;
  }, [emailSenders, searchTerm]);

  /**
   * Updates the senders name as the sender's email selection changes
   */
  useEffect(() => {
    if (isMountRef.current) {
      const {
        doubleLine: sendersName,
        data: { defaultReplyTo = watchedSendersEmail } = {},
      } = emailSenders.find(({ id }) => id === watchedSendersEmail) ?? {};

      if (sendersName) {
        setValue(fromNameFormField.key, sendersName);
      }
      setValue(replyToFormField.key, defaultReplyTo);
    }
    isMountRef.current = true;
  }, [watchedSendersEmail]);

  const InputFormField = useDynamicSenders ? (
    <TypeaheadSelectField name={sendersEmailFormField.key} options={emailSenders} isFullWidth />
  ) : (
    <BasicFilterSelectField
      name={sendersEmailFormField.key}
      options={memoizedSelectedOptions}
      onSearchChange={(searchedItem: string | undefined) => setSearchTerm(searchedItem ?? '')}
      disableInternalFiltering
      isDisabled={emailSenders.length === 0}
      isFullWidth
    />
  );

  return (
    <>
      <StyledVariantFormField labelText={fromNameFormField.label} isHorizontalLayout>
        {isFieldEditable ? (
          <InputField
            name={fromNameFormField.key}
            autoFocus={fromNameFormField.isFocused || false}
          />
        ) : (
          <ReadOnlyValue>{fieldValues[fromNameFormField.key]}</ReadOnlyValue>
        )}
      </StyledVariantFormField>
      <SelectFormFieldWrapper
        labelText={sendersEmailFormField.label}
        hasError={hasError}
        errorText={errorText}
        helpText={isFieldEditable ? helperText : ''}
        isHorizontalLayout
      >
        {isFieldEditable ? (
          InputFormField
        ) : (
          <ReadOnlyValue>{fieldValues[sendersEmailFormField.key]}</ReadOnlyValue>
        )}
      </SelectFormFieldWrapper>
      <HiddenVariantFormField labelText={replyToFormField.label} isHorizontalLayout>
        {isFieldEditable ? (
          <InputField name={replyToFormField.key} />
        ) : (
          <ReadOnlyValue>{fieldValues[replyToFormField.key]}</ReadOnlyValue>
        )}
      </HiddenVariantFormField>
    </>
  );
};
